/**
 * Privacy state for custom fields.
 *
 * A field is marked private when it is created; the builder only reads the
 * flag. Uploads to a private field are access-controlled, so submissions
 * expose them through the files service rather than a public url.
 */

/** Element or custom field shape carrying the server-owned privacy flag. */
export interface PrivacyAwareField {
  isPrivate?: boolean;
  model?: string;
  custom?: boolean;
  customEdited?: boolean;
}

/**
 * Whether the field's file access is already fixed.
 *
 * The API accepts file access only when the custom field is created, so the
 * control is editable exactly while the field is still new. This is the same
 * "custom field already created" condition the sibling editors receive as
 * `customFieldCreated`.
 *
 * @param element - Builder element or custom field to check
 * @returns True when file access can no longer be changed
 */
export const isFileAccessLocked = (
  element: PrivacyAwareField | null | undefined
): boolean => !(element?.custom && !element?.customEdited);

/**
 * Whether a field can have its file access chosen.
 *
 * Private uploads are only supported for contact fields today, so the option is
 * hidden for opportunity, business and custom object fields — offering it there
 * would create a field that silently is not private. Contact fields carry
 * either `model: "contact"` or no model at all.
 *
 * @param element - Builder element or custom field to check
 * @returns True when file access can be chosen for this field
 */
export const supportsFileAccessChoice = (
  element: PrivacyAwareField | null | undefined
): boolean => {
  if (!element) return false;
  const model = element.model;
  return !model || model === "contact";
};

/**
 * Checks whether a custom field is private.
 *
 * The flag rides on the element from the moment it is dropped on the canvas and
 * is persisted with the form definition, so the element is the only source that
 * needs consulting.
 *
 * @param element - Builder element or custom field to check
 * @returns True when uploads to this field are access-controlled
 */
export const isPrivateCustomField = (
  element: PrivacyAwareField | null | undefined
): boolean => element?.isPrivate === true;
